import {
    put,
    call,
    select,
    putResolve,
} from 'redux-saga/effects'

import Loader from 'common/modules/loader'
import Internal from 'common/modules/internal'
import {
    PICO_API_URL,
    FORMS_POPUP,
} from '@piconetworks/endpoints'
import { REQUEST } from '@piconetworks/network-middleware'

import { isEmpty } from 'lodash'
import { focusPrompt } from './frameUtilities'
import { creators } from '../actions'

const popupActions = {
    signup_popup: function* signup({ popupId }) {
        yield call(focusPrompt, '/registration', {
            popupId,
        })
    },
    payment_popup: function* paymentPopup({
        popupId,
        gift,
        optionId,
        isOfferCodeActive,
    }) {
        if (optionId) {
            yield call(focusPrompt, `/checkout-with-${gift ? 'gift' : 'plan'}`, {
                popupId,
                optionId,
                isOfferCodeActive,
            })
        } else if (gift) {
            yield call(focusPrompt, '/gift', {
                popupId,
                gift,
                isOfferCodeActive,
            })
        } else {
            yield call(focusPrompt, '/checkout', {
                popupId,
                gift,
                isOfferCodeActive,
            })
        }
    },
}

function* fetchLoaderPopup({ popupId, optionId }) {
    try {
        const publisherName = yield select(Loader.selectors.publisherUsername)

        if (!popupId && !optionId) {
            return null
        }

        const { data: popup } = yield putResolve({
            type: REQUEST,
            config: {
                method: 'GET',
                baseURL: PICO_API_URL(),
                url: `${FORMS_POPUP()}/${publisherName}/${popupId || optionId}`,
            },
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            timeout: 30000,
        })

        if (popup) {
            yield put(
                yield call(Loader.creators.fetchLoaderPopupSuccess, {
                    payload: {
                        popup: {
                            ...popup,
                            fetched: true,
                        },
                    },
                }),
            )

            yield put(Internal.creators.sendMessage({
                payload: {
                    target: '*',
                    ...Loader.creators.fetchLoaderPopupSuccess({
                        payload: {
                            popup: {
                                ...popup,
                                fetched: true,
                            },
                        },
                    }),
                },
            }))
        } else {
            throw new Error('Popup not found')
        }
        return popup
    } catch (e) {
        return null
    }
}

function* showPopup({
    payload: {
        popupId,
        gift,
        optionId,
        isOfferCodeActive = false,
    },
}) {
    try {
        if (!popupId && !optionId) {
            throw new Error('popup id was not provided')
        }

        let popup

        if (optionId) {
            // select the popup by option id
            popup = yield select(Loader.selectors.popupGetPopupByProductOptionId, optionId)
        } else {
            popup = yield select(Loader.selectors.popupGetPopupById, popupId)
        }

        // if popup doesnt exist
        // or if it was never fetched
        // then fetch it
        // this hydrates the loaders store
        if (isEmpty(popup)
            || !popup?.fetched
        ) {
            popup = yield call(fetchLoaderPopup, {
                popupId,
                optionId,
            })
        }

        if (!popup) {
            throw new Error('popup does not exist')
        }

        const { type } = popup

        const popupAction = popupActions[type]

        if (!popupAction) {
            throw new Error(`popup action could not be found for: ${type}`)
        }

        yield call(popupAction, {
            popupId: popup?.id,
            gift,
            optionId,
            isOfferCodeActive,
        })

        yield put(
            yield call(creators.showPopupSuccess),
        )
    } catch (error) {
        yield put(
            yield call(creators.showPopupError, {
                error: error?.message,
            }),
        )
    }
}

function* showDefaultPopup({ defaultType = '' }) {
    let popup
    if (defaultType === 'defaultAnon') {
        popup = yield select(Loader.selectors.getDefaultAnonPopup)
    } else if (defaultType === 'defaultRegistered') {
        popup = yield select(Loader.selectors.getDefaultRegisteredPopup)
    } else {
        popup = yield select(Loader.selectors.getDefaultPopup)
    }

    const popupId = popup?.id

    const popupType = popup?.type?.includes('payment') ? 'payment' : 'signup'

    yield call(popupActions[`${popupType}_popup`], { popupId })
}

export default showPopup

export {
    showDefaultPopup,
    fetchLoaderPopup,
}
